/**
 * F7.1 (tema do lojista): pure, environment-agnostic validation used by BOTH
 * `@vivian/loader` (the host-facing side — trigger button color, the outer
 * iframe shell's width/radius, and deciding what's even worth forwarding
 * through the theme handshake) and `@vivian/embed-app` (the iframe-side
 * consumer — see that package's `theme/applyTheme.ts`, which is also where
 * the accessibility gate on `--vivian-color-primary` — the focus ring AND
 * every consent/advisor control that reads the same token — actually gets
 * enforced). Living here, not duplicated in each package, guarantees both
 * sides agree byte-for-byte on what counts as "a valid color" or "enough
 * contrast" — see this monorepo's `shared-contracts` doc comment convention
 * (already the single source of truth for `VIVIAN_EVENTS`/handshake message
 * types) for why that matters more than it might look like at a glance.
 *
 * `parseCssColor` deliberately does NOT lean on the browser's own CSS
 * parser (e.g. a throwaway DOM element + `getComputedStyle`), even though
 * every real target environment (a real browser at runtime; jsdom in this
 * monorepo's own test suites) has one available. Two reasons:
 * 1. This package's own test environment is plain Node (`vitest.config.ts`:
 *    `environment: "node"`) — no DOM at all — and changing that just for
 *    this one module would mean adding a jsdom devDependency to a package
 *    that otherwise has none, for a single feature.
 * 2. A validator whose result can differ between jsdom (test time) and a
 *    real browser (run time) is worse than one that's simply narrower but
 *    consistent everywhere — a host whose theme color silently validates
 *    differently in CI than in production is a strictly worse failure mode
 *    than "we don't recognize hsl() yet."
 *
 * Recognizes hex (`#rgb`/`#rgba`/`#rrggbb`/`#rrggbbaa`) and `rgb()`/`rgba()`
 * (comma- or space-separated, CSS Color 4 style) — between them, what every
 * mainstream color picker / design tool outputs. Anything else (named CSS
 * colors, `hsl()`, `oklch()`, …) is treated as unrecognized, which is
 * always the SAFE direction for a token that gates accessibility: it falls
 * back to the platform default rather than risking a value this module
 * can't actually verify the contrast of.
 *
 * P0 fix (architecture-gate finding, critical): `parseCssColor` REJECTS
 * (returns `null`) any color that isn't fully opaque — see its own doc
 * comment below. An earlier revision parsed the alpha channel and then
 * threw it away, which let `#6b21ff00` (the shipped default, alpha ZERO)
 * measure as the OPAQUE `#6b21ff` — passing the accessibility gate at
 * ~6.4:1 — while `applyTheme.ts` still writes the ORIGINAL (transparent)
 * string as the live custom property. Net effect: a one-character change
 * to a theme value silently produced `outline: 2px solid transparent` and
 * backgroundless buttons — exactly the failure mode this whole gate exists
 * to prevent. Compositing over a known background (white) instead of
 * rejecting would fix that specific case but would be WRONG for
 * `@vivian/loader`'s trigger button, which reads the SAME `parseCssColor`
 * against a host page background this module has no way to know — so
 * rejection, not composition, is the one answer that's correct on both
 * sides of this shared module.
 *
 * SECURITY NOTE (also from that review): this parser being an ALLOWLIST —
 * "these exact syntaxes and nothing else" — is doing real security work
 * that isn't obvious from the accessibility framing above.
 * `ConsentGate.tsx` (and others) write `--vivian-color-primary` into a
 * `background:` CSS property, which is SHORTHAND — it accepts `<image>`,
 * including `url(...)`. If this parser were ever loosened into a denylist
 * ("reject known-bad patterns") instead of staying an allowlist ("accept
 * only known-good syntax"), `--vivian-color-primary: url(https://evil/?x)`
 * would sail through as "just an unrecognized-but-harmless string" and
 * become a live network request fired from the embed app's own origin the
 * moment a themed button renders. Nothing today's denylist-shaped thinking
 * would catch, because `url(...)` isn't inherently malicious syntax — it's
 * only wrong HERE, in a context expecting a color. Any future format
 * support (e.g. `hsl()`) MUST be added as a new, explicit, fully-anchored
 * pattern in the allowlist below — never by relaxing/removing a check.
 */
/**
 * Normalizes a CSS length override into a value the browser can use
 * directly: a bare number is treated as pixels (matching how plenty of
 * HTML sizing attributes already work, e.g. `<img width>`), and a number
 * with a recognized unit is passed through unchanged. Returns `null` for
 * anything else so the caller can fall back to its own default instead of
 * handing the browser a nonsensical inline style value.
 *
 * F7.1: moved here from `@vivian/loader`'s `use-vivian-element.ts` (where it
 * originally validated `height`/`max-width`) so `@vivian/embed-app` can
 * reuse the EXACT same rules for `--vivian-radius` instead of growing a
 * second, potentially-drifting length validator.
 *
 * devsecops-validator review (MÉDIO fix): `value` is typed `unknown`, not
 * `string`, on purpose — every OTHER caller in this monorepo does always
 * pass an actual string (a `getAttribute`/`getComputedStyle` read, always a
 * string in the DOM), but `@vivian/embed-app`'s `applyTheme.ts` calls this
 * with `tokens.radius`, sourced from a postMessage payload. `ThemeTokens`
 * DECLARES that field `string | undefined`, but that's a TypeScript-only
 * contract that does not survive structured clone — a compromised/
 * misconfigured host can send `{ radius: 123 }` and this function receives
 * a number at runtime despite the static type. This used to call
 * `value.trim()` as its very first line with no runtime guard at all,
 * throwing a `TypeError` with no try/catch anywhere in the real call chain
 * (`themeChannel.ts` -> `applyTheme.ts` -> `App.tsx`) — directly
 * contradicting `applyTheme.ts`'s own doc comment ("Never throws, regardless
 * of what garbage a compromised/misconfigured host sends"). The `typeof`
 * guard below is the actual fix; `unknown` (over leaving it `string`) makes
 * that guard something the type checker can verify actually narrows, rather
 * than a runtime check for a case TypeScript claims can't happen.
 */
export function parseCssLength(value) {
    if (typeof value !== "string")
        return null;
    const trimmed = value.trim();
    if (/^\d+(\.\d+)?$/.test(trimmed))
        return `${trimmed}px`;
    if (/^\d+(\.\d+)?(px|%|vh|vw|rem|em)$/.test(trimmed))
        return trimmed;
    return null;
}
const HEX_COLOR_RE = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
// Capturing group 4 (the alpha token) is OPTIONAL and, unlike groups 1-3,
// may be `undefined` on a match — see the P0 doc comment above for why its
// value (when present) is now actually inspected instead of discarded.
const RGB_FUNCTION_RE = /^rgba?\(\s*(-?[\d.]+%?)\s*[,\s]\s*(-?[\d.]+%?)\s*[,\s]\s*(-?[\d.]+%?)\s*(?:[,/]\s*(-?[\d.]+%?)\s*)?\)$/i;
function clampByte(value) {
    return Math.min(255, Math.max(0, Math.round(value)));
}
/** Parses a single `rgb()`/`rgba()` channel token — either a 0-255 number or a `NN%` percentage — into a clamped 0-255 byte. */
function parseChannel(token) {
    if (token.endsWith("%")) {
        const pct = Number.parseFloat(token.slice(0, -1));
        return clampByte((pct / 100) * 255);
    }
    return clampByte(Number.parseFloat(token));
}
/**
 * Whether an alpha token (a bare `rgba()`/`rgb(... / ...)` alpha value,
 * e.g. `"0.8"`, `"1"`, `"100%"`) represents FULL opacity — P0 fix's answer
 * to "is this color allowed to parse at all". CSS clamps alpha to `[0, 1]`,
 * so any value that resolves to `>= 1` counts as opaque (covers `1`, `1.0`,
 * `100%`, and even a nonsensical `>1`/`>100%` the browser would itself clamp
 * down to fully opaque) — anything strictly less is a real, visible amount
 * of transparency and must be rejected.
 */
function isOpaqueAlphaToken(token) {
    const value = token.endsWith("%") ? Number.parseFloat(token.slice(0, -1)) / 100 : Number.parseFloat(token);
    return value >= 1;
}
/** Whether a hex alpha byte (0-255, already parsed) represents full opacity — the hex-syntax counterpart to {@link isOpaqueAlphaToken}. */
function isOpaqueAlphaByte(byte) {
    return byte === 255;
}
/**
 * Parses a CSS color string into RGB (0-255 per channel), or `null` if it
 * isn't one of the recognized formats OR isn't fully opaque (see this
 * file's own doc comment — P0 fix — for why partial transparency is a
 * rejection, not a "parse it and drop the alpha" case). Never throws.
 *
 * devsecops-validator review (MÉDIO fix): `value` is `unknown`, not
 * `string` — see {@link parseCssLength}'s doc comment for the full
 * rationale (same postMessage-boundary type-confusion issue, same fix). A
 * malformed `--vivian-color-primary` (e.g. `{ colorPrimary: 123 }` from a
 * compromised/misconfigured host) used to throw a `TypeError` from
 * `value.trim()` here — reached via `evaluateContrast`/`pickOnColor`, both
 * of which `applyTheme.ts` calls directly on the unvalidated payload field.
 */
export function parseCssColor(value) {
    if (typeof value !== "string")
        return null;
    const trimmed = value.trim();
    const hexMatch = HEX_COLOR_RE.exec(trimmed);
    if (hexMatch) {
        // Non-null assertions below: HEX_COLOR_RE has exactly one capturing
        // group, so a successful match always populates hexMatch[1] — TS's
        // `noUncheckedIndexedAccess` can't see that regex-capture invariant on
        // its own, hence the `!`s rather than a runtime check that can't fail.
        const hex = hexMatch[1];
        if (hex.length === 3) {
            // #rgb — no alpha nibble at all, always fully opaque.
            const r = Number.parseInt(hex[0] + hex[0], 16);
            const g = Number.parseInt(hex[1] + hex[1], 16);
            const b = Number.parseInt(hex[2] + hex[2], 16);
            return { r, g, b };
        }
        if (hex.length === 4) {
            // #rgba — each nibble doubled (e.g. "f" -> "ff"). P0: REJECT unless
            // the alpha nibble doubles to a fully-opaque byte (0xff) — see this
            // file's top doc comment for the concrete `#6b21ff...`-style exploit
            // this closes.
            const alphaByte = Number.parseInt(hex[3] + hex[3], 16);
            if (!isOpaqueAlphaByte(alphaByte))
                return null;
            const r = Number.parseInt(hex[0] + hex[0], 16);
            const g = Number.parseInt(hex[1] + hex[1], 16);
            const b = Number.parseInt(hex[2] + hex[2], 16);
            return { r, g, b };
        }
        if (hex.length === 6) {
            // #rrggbb — no alpha byte at all, always fully opaque.
            const r = Number.parseInt(hex.slice(0, 2), 16);
            const g = Number.parseInt(hex.slice(2, 4), 16);
            const b = Number.parseInt(hex.slice(4, 6), 16);
            return { r, g, b };
        }
        // #rrggbbaa (hex.length === 8) — P0: REJECT unless the alpha byte
        // (bytes 7-8) is fully opaque (0xff).
        const alphaByte = Number.parseInt(hex.slice(6, 8), 16);
        if (!isOpaqueAlphaByte(alphaByte))
            return null;
        const r = Number.parseInt(hex.slice(0, 2), 16);
        const g = Number.parseInt(hex.slice(2, 4), 16);
        const b = Number.parseInt(hex.slice(4, 6), 16);
        return { r, g, b };
    }
    const rgbMatch = RGB_FUNCTION_RE.exec(trimmed);
    if (rgbMatch) {
        // P0: the 4th capturing group (alpha) is OPTIONAL — `undefined` means
        // no alpha was written at all (`rgb(...)`/`rgba(...)` with only 3
        // channels), which is unambiguously fully opaque; when it IS present,
        // REJECT unless it resolves to full opacity.
        const alphaToken = rgbMatch[4];
        if (alphaToken !== undefined && !isOpaqueAlphaToken(alphaToken))
            return null;
        // Same non-null-assertion rationale as the hex branch above: the three
        // channel capturing groups are required (non-optional) and always
        // populate on a match.
        return {
            r: parseChannel(rgbMatch[1]),
            g: parseChannel(rgbMatch[2]),
            b: parseChannel(rgbMatch[3]),
        };
    }
    return null;
}
/** One channel's contribution to WCAG relative luminance — see https://www.w3.org/TR/WCAG21/#dfn-relative-luminance. */
function linearizeChannel(byte) {
    const s = byte / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}
/** WCAG relative luminance of an RGB color, in `[0, 1]` (0 = black, 1 = white). */
export function relativeLuminance(color) {
    return 0.2126 * linearizeChannel(color.r) + 0.7152 * linearizeChannel(color.g) + 0.0722 * linearizeChannel(color.b);
}
/** WCAG contrast ratio between two colors, in `[1, 21]` — symmetric (argument order doesn't matter, per the spec's own `(lighter + 0.05) / (darker + 0.05)` formula). */
export function contrastRatio(a, b) {
    const lA = relativeLuminance(a);
    const lB = relativeLuminance(b);
    const lighter = Math.max(lA, lB);
    const darker = Math.min(lA, lB);
    return (lighter + 0.05) / (darker + 0.05);
}
/**
 * WCAG 2.1 Success Criterion 1.4.11 ("Non-text Contrast")'s minimum ratio
 * for UI components and their visual boundaries — the category a focus
 * indicator falls under. Used (not the stricter 4.5:1 normal-text AA
 * minimum) as the ADMISSION bar for `--vivian-color-primary` itself in
 * `embed-app` — the focus ring, button/badge backgrounds, and borders all
 * gate on this SAME check against the SAME token, deliberately: see
 * `applyTheme.ts`'s doc comment for why one check covering all of those is
 * the chosen design, not an oversight.
 *
 * P2 fix (architecture-gate finding): this is NOT the bar for uses of
 * `--vivian-color-primary` as TEXT (`color:`, not `background:`/`border:`)
 * — those need WCAG 1.4.3's 4.5:1 instead, via the separate
 * `MIN_TEXT_CONTRAST_RATIO`/`--vivian-color-primary-text` pair below. This
 * constant stays at 3:1 either way — see `MIN_TEXT_CONTRAST_RATIO`'s own
 * doc comment for why raising this one instead would be the wrong fix.
 */
export const MIN_UI_CONTRAST_RATIO = 3;
/**
 * WCAG 2.1 Success Criterion 1.4.3 ("Contrast (Minimum)")'s bar for
 * normal-sized text — P2 fix. `MIN_UI_CONTRAST_RATIO` (3:1) is correct for
 * `--vivian-color-primary` used as a BACKGROUND/border (buttons, badges,
 * the focus ring's own outline color against this app's page background) —
 * but several components also use that SAME token as `color:`, i.e. TEXT
 * drawn on top of a DIFFERENT (typically white) background, which is WCAG
 * 1.4.3's territory, not 1.4.11's. A real, non-hypothetical brand color
 * (`#F96302`-class oranges) clears 3:1 comfortably (~3.08:1) but falls
 * short of 4.5:1 — deliberately kept as a SEPARATE constant/gate rather
 * than raising `MIN_UI_CONTRAST_RATIO` itself: doing that would reject
 * such a color for background/border use too, where 3:1 was always the
 * right bar and always will be. See `applyTheme.ts`'s doc comment for how
 * this drives the separate `--vivian-color-primary-text` token.
 */
export const MIN_TEXT_CONTRAST_RATIO = 4.5;
/**
 * Evaluates `colorValue` against `backgroundValue` at `minRatio`, same
 * inputs/logic as {@link meetsMinimumContrast}, but returns WHY a failure
 * happened instead of collapsing every failure to `false` — see
 * {@link ContrastFailureReason}. `meetsMinimumContrast` is kept as a thin
 * wrapper around this (below) for every existing caller that only cares
 * about the yes/no answer.
 */
export function evaluateContrast(colorValue, backgroundValue, minRatio = MIN_UI_CONTRAST_RATIO) {
    const color = parseCssColor(colorValue);
    const background = parseCssColor(backgroundValue);
    if (!color || !background)
        return { ok: false, reason: "unrecognized" };
    if (contrastRatio(color, background) >= minRatio)
        return { ok: true };
    return { ok: false, reason: "low-contrast" };
}
/**
 * Whether `colorValue` has at least `minRatio` contrast against
 * `backgroundValue`. `false` (never a thrown error) whenever either color
 * fails to parse — an unrecognized/malformed value is treated exactly like
 * "not enough contrast", so callers that don't need to distinguish the two
 * (see {@link evaluateContrast} for callers that do) have exactly one
 * failure branch to handle (fall back to the safe default) instead of two.
 */
export function meetsMinimumContrast(colorValue, backgroundValue, minRatio = MIN_UI_CONTRAST_RATIO) {
    return evaluateContrast(colorValue, backgroundValue, minRatio).ok;
}
/** `--vivian-color-on-primary`'s two possible values — see {@link pickOnColor}. `#111111` (not pure `#000000`) matches this app's own body text color (`embed-app/index.html`), so a themed button's text reads consistently with the rest of the page's near-black. */
const ON_COLOR_WHITE = "#ffffff";
const ON_COLOR_BLACK = "#111111";
/**
 * Picks whichever of white (`#ffffff`) or near-black (`#111111`) gives
 * BETTER contrast on top of `colorValue` — the value for
 * `--vivian-color-on-primary` (P1 fix): the color to draw TEXT/icons in
 * when an element's own BACKGROUND is `colorValue`.
 *
 * Why this needs to exist at all: `meetsMinimumContrast`/
 * `MIN_UI_CONTRAST_RATIO` (3:1) gates `--vivian-color-primary` against
 * this app's fixed white PAGE background — the right bar for admitting a
 * color as a background/border/swatch at all. That is a DIFFERENT pairing
 * from white TEXT drawn ON TOP OF that same color (a button's label),
 * which needs WCAG 1.4.3's 4.5:1. A real brand color
 * (`#F96302`-class oranges, ~3.08:1 against white — clears the 3:1 admission
 * gate) produces illegible ~2.9:1 white-on-orange text if every consumer
 * keeps hardcoding `color: "#fff"`. Raising the admission gate itself to
 * 4.5:1 would reject that color entirely (including for background/border
 * uses where 3:1 was always the right bar) and hand every such tenant back
 * the VIVIAN purple instead — not the fix. Instead, every consumer that
 * draws TEXT on `--vivian-color-primary` reads THIS derived token (falling
 * back to `#fff`, unchanged, when unset) so the label always gets
 * whichever of black/white actually reads better on that specific color —
 * independent of, and without touching, the 3:1 admission gate.
 *
 * Returns `#ffffff` for a `colorValue` this module can't parse — matches
 * the "when in doubt, don't change behavior" posture of every other themed
 * token. In practice this is only ever called with a value that has
 * ALREADY cleared `meetsMinimumContrast` against white (see
 * `applyTheme.ts`/`use-vivian-element.ts`), so an unparseable input here
 * would itself indicate a caller bug, not a real host-supplied value.
 */
export function pickOnColor(colorValue) {
    const color = parseCssColor(colorValue);
    if (!color)
        return ON_COLOR_WHITE;
    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 17, g: 17, b: 17 };
    return contrastRatio(color, black) > contrastRatio(color, white) ? ON_COLOR_BLACK : ON_COLOR_WHITE;
}
/**
 * devsecops-validator review (BAIXO fix): exported so `@vivian/loader`'s
 * `use-vivian-element.ts` can apply the SAME ceiling on the SEND side, not
 * just here on the receive side. Before this, `isSafeFontFamily` was the
 * only consumer — `colorPrimary`/`radius` had no length cap at all, so an
 * absurdly large custom-property value would be serialized whole and posted
 * across the iframe boundary before ever being validated/discarded here.
 * Not a vulnerability fix (every parser in this file is an anchored regex —
 * confirmed no ReDoS risk even on multi-MB input), just bandwidth hygiene:
 * no reason to let a value this module will reject anyway travel the wire
 * at full size first.
 */
export const MAX_FONT_FAMILY_LENGTH = 200;
// P3 fix (architecture-gate finding): this used to be a DENYLIST
// (`/[{}<>;]/` — reject a handful of "obviously CSS/HTML structural"
// characters) that let anything else through, including pure garbage like
// `"!!!"` — non-empty, short, none of the denied characters, so it PASSED
// and was written as the literal `font-family` value. Since `!!!` isn't a
// valid `<family-name>`, the resulting computed `font-family` declaration
// (`var(--vivian-font-family, system-ui, sans-serif)`, now resolving to a
// garbage first item) becomes INVALID — and CSS's handling of an invalid
// property value is to act as if the whole declaration were never set,
// i.e. `font-family` INHERITS from its parent rather than falling through
// to the `var()` call's own fallback (`system-ui, sans-serif`). In a
// browser, that usually means the platform's default serif, not this
// app's intended `system-ui` — quietly contradicting this module's own
// "falls back to the platform default unchanged" contract. Same
// allowlist-not-denylist principle `parseCssColor`'s doc comment spells
// out: enumerate what a `<family-name>` list plausibly looks like (CSS
// identifier characters, digits, spaces, hyphens, quotes, commas — every
// character that appears anywhere in this file's own `Arial, sans-serif` /
// `"Helvetica Neue", Arial, sans-serif` / `system-ui` examples), reject
// everything else, INCLUDING characters nobody has thought to deny yet.
const SAFE_FONT_FAMILY_CHARS_RE = /^[\p{L}\p{N}\s,'"-]+$/u;
/**
 * Light-touch sanitization for `--vivian-font-family`: not a real CSS
 * `<family-name>` parser (there's no accessibility property at stake here
 * the way there is for color, so this isn't trying to be one) — just
 * rejects empty/absurdly-long values and, via an ALLOWLIST (see this
 * constant's own doc comment for why not a denylist), anything containing
 * a character with no legitimate use inside a `font-family` value list.
 * Defense in depth: setting a CSS custom property via the CSSOM
 * (`style.setProperty`, which is how `applyTheme.ts` applies this) can't
 * itself be used to "break out" into other declarations/rules the way raw
 * string concatenation into a stylesheet's `textContent` could — but
 * rejecting anything outside this allowlist costs nothing and removes any
 * doubt, for both that (defense in depth) and the "resolves to something
 * that isn't quietly invalid" reason above.
 *
 * devsecops-validator review (MÉDIO fix): `value` is `unknown`, not
 * `string` — see {@link parseCssLength}'s doc comment for the full
 * rationale (same postMessage-boundary type-confusion issue, same fix).
 */
export function isSafeFontFamily(value) {
    if (typeof value !== "string")
        return false;
    const trimmed = value.trim();
    if (!trimmed)
        return false;
    if (trimmed.length > MAX_FONT_FAMILY_LENGTH)
        return false;
    if (!SAFE_FONT_FAMILY_CHARS_RE.test(trimmed))
        return false;
    return true;
}
//# sourceMappingURL=theme.js.map